import React, { useState } from 'react';
import { Product, Ingredient, Language, SavedLabel } from '../types';
import { ProductForm } from './ProductForm';
import { LanguageSelector } from './LanguageSelector';
import { LabelDesigner } from './LabelDesigner';
import { AllergenBadge } from './AllergenBadge';
import { Plus, Pencil, Trash2, ShoppingBag, Package, Tag, Eye, RefreshCw } from 'lucide-react';
import { LANGUAGES } from '../utils/translations';
interface ProductListProps {
  products: Product[];
  ingredients: Ingredient[];
  onAdd: (product: Omit<Product, 'id' | 'createdAt'>) => void;
  onEdit: (id: string, product: Omit<Product, 'id' | 'createdAt'>) => void;
  onDelete: (id: string) => void;
  onSaveLabel: (productId: string, label: Omit<SavedLabel, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onRefreshAllLabels: (productId: string) => void;
}
export function ProductList({
  products,
  ingredients,
  onAdd,
  onEdit,
  onDelete,
  onSaveLabel,
  onRefreshAllLabels
}: ProductListProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [selectedProductForLabel, setSelectedProductForLabel] = useState<Product | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  const [editingLabel, setEditingLabel] = useState<SavedLabel | null>(null);
  const handleSave = (product: Omit<Product, 'id' | 'createdAt'>) => {
    if (editingProduct) {
      onEdit(editingProduct.id, product);
    } else {
      onAdd(product);
    }
    setShowForm(false);
    setEditingProduct(null);
  };
  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setShowForm(true);
  };
  const handleCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
  };
  const handleCreateLabel = (product: Product) => {
    setSelectedProductForLabel(product);
    setShowLanguageSelector(true);
    setEditingLabel(null);
  };
  const handleEditLabel = (product: Product, label: SavedLabel) => {
    setSelectedProductForLabel(product);
    setSelectedLanguage(label.language);
    setEditingLabel(label);
  };
  const handleLanguageSelect = (language: Language) => {
    setSelectedLanguage(language);
    setShowLanguageSelector(false);
  };
  const handleCloseLabelDesigner = () => {
    setSelectedProductForLabel(null);
    setSelectedLanguage(null);
    setEditingLabel(null);
  };
  const handleSaveLabelFromDesigner = (label: Omit<SavedLabel, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (selectedProductForLabel) {
      onSaveLabel(selectedProductForLabel.id, label);
    }
  };
  const handleRefreshLabels = (productId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Alle gespeicherten Etiketten mit aktuellen Produktdaten aktualisieren?')) {
      onRefreshAllLabels(productId);
    }
  };
  const getProductIngredients = (product: Product) => {
    return product.ingredientIds.map(id => ingredients.find(i => i.id === id)).filter(Boolean) as Ingredient[];
  };
  const getProductAllergens = (product: Product) => {
    const allergens = new Set<string>();
    getProductIngredients(product).forEach(ingredient => {
      ingredient.allergens.forEach(allergen => allergens.add(allergen));
    });
    return Array.from(allergens);
  };
  return <div data-id="element-313">
      <div className="flex items-center justify-between mb-6" data-id="element-314">
        <div data-id="element-315">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-316">Produkte</h2>
          <p className="text-gray-600 mt-1" data-id="element-317">
            Verwalten Sie Ihre Produkte mit Zutaten und Allergenen
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" disabled={ingredients.length === 0} data-id="element-318">
          <Plus className="w-5 h-5" data-id="element-319" />
          Neues Produkt
        </button>
      </div>

      {ingredients.length === 0 ? <div className="text-center py-12 bg-amber-50 rounded-lg border-2 border-dashed border-amber-300" data-id="element-320">
          <Package className="w-12 h-12 text-amber-600 mx-auto mb-3" data-id="element-321" />
          <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-322">
            Keine Zutaten vorhanden
          </h3>
          <p className="text-gray-600" data-id="element-323">
            Erstellen Sie zuerst Zutaten, bevor Sie Produkte anlegen können
          </p>
        </div> : products.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" data-id="element-324">
          <ShoppingBag className="w-12 h-12 text-gray-400 mx-auto mb-3" data-id="element-325" />
          <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-326">
            Keine Produkte vorhanden
          </h3>
          <p className="text-gray-600 mb-4" data-id="element-327">Erstellen Sie Ihr erstes Produkt</p>
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-id="element-328">
            <Plus className="w-5 h-5" data-id="element-329" />
            Produkt hinzufügen
          </button>
        </div> : <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-330">
          {products.map(product => {
        const productIngredients = getProductIngredients(product);
        const productAllergens = getProductAllergens(product);
        return <div key={product.id} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow" data-id="element-331">
                <div className="flex items-start justify-between mb-4" data-id="element-332">
                  <div className="flex-1" data-id="element-333">
                    <h3 className="font-semibold text-gray-900 text-xl mb-1" data-id="element-334">
                      {product.name}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-335">
                      {productIngredients.length}{' '}
                      {productIngredients.length === 1 ? 'Zutat' : 'Zutaten'}
                    </p>
                  </div>
                  <div className="flex gap-1" data-id="element-336">
                    <button onClick={() => handleCreateLabel(product)} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors" title="Neues Etikett erstellen" data-id="element-337">
                      <Tag className="w-4 h-4" data-id="element-338" />
                    </button>
                    <button onClick={() => handleEdit(product)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" data-id="element-339">
                      <Pencil className="w-4 h-4" data-id="element-340" />
                    </button>
                    <button onClick={() => {
                if (confirm(`Produkt "${product.name}" wirklich löschen?`)) {
                  onDelete(product.id);
                }
              }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" data-id="element-341">
                      <Trash2 className="w-4 h-4" data-id="element-342" />
                    </button>
                  </div>
                </div>

                {productIngredients.length > 0 && <div className="mb-4" data-id="element-343">
                    <h4 className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-344">
                      Zutaten
                    </h4>
                    <div className="flex flex-wrap gap-2" data-id="element-345">
                      {productIngredients.map(ingredient => <span key={ingredient.id} className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-sm" data-id="element-346">
                          {ingredient.name}
                        </span>)}
                    </div>
                  </div>}

                {productAllergens.length > 0 && <div className="mb-4 pt-4 border-t border-gray-200" data-id="element-347">
                    <h4 className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-348">
                      Allergene
                    </h4>
                    <div className="flex flex-wrap gap-2" data-id="element-349">
                      {productAllergens.map(allergenId => <AllergenBadge key={allergenId} allergenId={allergenId} size="sm" data-id="element-350" />)}
                    </div>
                  </div>}

                {product.labels && product.labels.length > 0 && <div className="pt-4 border-t border-gray-200" data-id="element-351">
                    <div className="flex items-center justify-between mb-2" data-id="element-352">
                      <h4 className="text-xs font-medium text-gray-500 uppercase" data-id="element-353">
                        Gespeicherte Etiketten ({product.labels.length})
                      </h4>
                      <button onClick={e => handleRefreshLabels(product.id, e)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 transition-colors" title="Alle Etiketten mit aktuellen Daten aktualisieren" data-id="element-354">
                        <RefreshCw className="w-3 h-3" data-id="element-355" />
                        <span data-id="element-356">Alle aktualisieren</span>
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2" data-id="element-357">
                      {product.labels.map(label => <button key={label.id} onClick={() => handleEditLabel(product, label)} className="relative group overflow-hidden rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-all" data-id="element-358">
                          <img src={label.thumbnail} alt={`Etikett ${label.language}`} className="w-full h-32 object-cover" data-id="element-359" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center" data-id="element-360">
                            <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-361" />
                          </div>
                          <div className="absolute top-2 right-2" data-id="element-362">
                            <span className="px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded" data-id="element-363">
                              {LANGUAGES[label.language].flag}{' '}
                              {label.language.toUpperCase()}
                            </span>
                          </div>
                        </button>)}
                    </div>
                  </div>}
              </div>;
      })}
        </div>}

      {showForm && <ProductForm product={editingProduct || undefined} ingredients={ingredients} onSave={handleSave} onCancel={handleCancel} data-id="element-364" />}

      {showLanguageSelector && selectedProductForLabel && <LanguageSelector onSelect={handleLanguageSelect} onCancel={() => {
      setShowLanguageSelector(false);
      setSelectedProductForLabel(null);
    }} data-id="element-365" />}

      {selectedProductForLabel && selectedLanguage && <LabelDesigner product={selectedProductForLabel} ingredients={ingredients} language={selectedLanguage} existingLabel={editingLabel || undefined} onClose={handleCloseLabelDesigner} onSave={handleSaveLabelFromDesigner} data-id="element-366" />}
    </div>;
}